import { useState, useContext, useEffect } from 'react';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Switch } from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import { NavContext } from '@/components/NavContext';
import MobileSubPageMenu from '@/components/HeaderMobile/MobileSubPageMenu';
import { useMobile } from '@/util';

const notificationsDisabledByDefault = new Set([
  'competenceacquired',
  'competencefailed',
]);

function EmailsAndNotifications() {
  const { t } = useTranslation();
  const { auth, setAuth } = useContext(AuthContext);
  const { setSubPage } = useContext(NavContext);
  const isMobile = useMobile();
  const authUser: any = auth?.user;

  const userRole: any = authUser?.role || 0;

  const [notifcationSettings, setNotifcationSettings] = useState<
    Record<string, boolean>
  >(authUser?.notifcationSettings || {});
  const [notificationEmailSettings, setNotificationEmailSettings] = useState<
    Record<string, boolean>
  >(authUser?.notificationEmailSettings || {});

  useEffect(() => {
    setSubPage(true);
    return () => setSubPage(false);
  }, []);

  const handleNotifications = (check: boolean, types: string | string[]) => {
    const settingTypes = Array.isArray(types) ? types : [types];
    const updatedSettings = { ...notifcationSettings };

    settingTypes.forEach((type) => {
      const defaultValue = !notificationsDisabledByDefault.has(type);

      if (check === defaultValue) delete updatedSettings[type];
      else updatedSettings[type] = check;
    });

    setNotifcationSettings(updatedSettings);

    axios.put(`${config.backend}/user/${authUser?.id}`, {
      id: authUser?.id,
      notifcationSettings: updatedSettings,
    });

    setAuth({
      user: Object.assign({}, auth?.user, {
        notifcationSettings: updatedSettings,
      }),
      token: auth?.token,
    });
  };

  const notificationEnabled = (types: string | string[]) => {
    const settingTypes = Array.isArray(types) ? types : [types];
    return settingTypes.every((type) => {
      const explicitValue = notifcationSettings[type];
      return explicitValue ?? !notificationsDisabledByDefault.has(type);
    });
  };

  const handleNotificationEmails = (
    check: boolean,
    types: string | string[]
  ) => {
    const settingTypes = Array.isArray(types) ? types : [types];
    const updatedSettings = { ...notificationEmailSettings };

    settingTypes.forEach((type) => {
      if (check) updatedSettings[type] = true;
      else delete updatedSettings[type];
    });

    setNotificationEmailSettings(updatedSettings);

    axios.put(`${config.backend}/user/${authUser?.id}`, {
      id: authUser?.id,
      notificationEmailSettings: updatedSettings,
    });

    setAuth({
      user: Object.assign({}, auth?.user, {
        notificationEmailSettings: updatedSettings,
      }),
      token: auth?.token,
    });
  };

  const notificationEmailEnabled = (types: string | string[]) => {
    const settingTypes = Array.isArray(types) ? types : [types];
    return settingTypes.every(
      (type) => notificationEmailSettings[type] === true
    );
  };

  const notificationSetting = (
    label: string,
    types: string | string[],
    testId: string,
    showEmail = false
  ) => (
    <div className="mt-2 flex flex-col gap-4 rounded-lg border border-grey-02 p-4 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1">
        <h4 className="font-normal">{label}</h4>
      </div>
      <div className="flex shrink-0 items-center gap-5">
        <div className="flex items-center gap-2">
          <span className="text-sm text-grey-06">
            {t('pages.profile_emails_notifications.in_app')}
          </span>
          <div data-testid={testId}>
            <Switch
              checked={notificationEnabled(types)}
              onChange={(isCheck) => handleNotifications(isCheck, types)}
            />
          </div>
        </div>
        {showEmail && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-grey-06">
              {t('pages.profile_emails_notifications.email')}
            </span>
            <div data-testid={`${testId}-email`}>
              <Switch
                checked={notificationEmailEnabled(types)}
                onChange={(isCheck) => handleNotificationEmails(isCheck, types)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {isMobile ? (
        <div className="mb-2">
          <MobileSubPageMenu title={'links.emails_notifications'} />
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h1 className="my-6 text-xl font-semibold text-black">
            {t('links.emails_notifications')}
          </h1>
        </div>
      )}
      <div className="flex flex-col">
        <div className="rounded-lg border border-grey-02 p-6">
          <div className="flex max-w-xl flex-col">
            <p className="pb-6 text-base font-semibold text-black">
              {t('pages.profile_emails_notifications.title')}
            </p>
            <p className="pb-2 text-base font-semibold text-black">
              {t('pages.profile_emails_notifications.notification_settings')}
            </p>
            <p className="pb-2 text-sm text-grey-06">
              {t('pages.profile_emails_notifications.email_description')}
            </p>

            <p className="pb-1 pt-5 text-sm font-semibold text-grey-06">
              {t('pages.profile_emails_notifications.category_general')}
            </p>
            {notificationSetting(
              t('pages.profile_emails_notifications.general_information'),
              'message',
              'notification-message'
            )}
            {notificationSetting(
              t('pages.profile_emails_notifications.new_platform_features'),
              'changelog',
              'notification-changelog'
            )}

            <p className="pb-1 pt-6 text-sm font-semibold text-grey-06">
              {t('pages.profile_emails_notifications.category_learn')}
            </p>
            {notificationSetting(
              t('pages.profile_emails_notifications.added_to_course'),
              'newcourse',
              'notification-newcourse',
              true
            )}
            {notificationSetting(
              t('pages.profile_emails_notifications.corrected_lessons'),
              'lessoncorrected',
              'notification-lessoncorrected'
            )}
            {notificationSetting(
              t('pages.profile_emails_notifications.corrected_course'),
              'coursecorrected',
              'notification-coursecorrected'
            )}

            {userRole >= 20 && (
              <>
                <p className="pb-1 pt-6 text-sm font-semibold text-grey-06">
                  {t('pages.profile_emails_notifications.category_create')}
                </p>
                {notificationSetting(
                  t('pages.profile_emails_notifications.publication_updated'),
                  'publicationupdated',
                  'notification-publicationupdated'
                )}
              </>
            )}

            {userRole >= 20 && (
              <>
                <p className="pb-1 pt-6 text-sm font-semibold text-grey-06">
                  {t('pages.profile_emails_notifications.category_follow_up')}
                </p>
                {notificationSetting(
                  t(
                    'pages.profile_emails_notifications.student_completed_course'
                  ),
                  'coursecomplete',
                  'notification-coursecomplete',
                  true
                )}
                {userRole > 21 && (
                  <>
                    {notificationSetting(
                      t(
                        'pages.profile_emails_notifications.competence_expired'
                      ),
                      'competenceexpired',
                      'notification-competence-expired',
                      true
                    )}
                    {notificationSetting(
                      t(
                        'pages.profile_emails_notifications.new_correction_tasks'
                      ),
                      'tocorrect',
                      'notification-tocorrect',
                      true
                    )}
                    {notificationSetting(
                      t('pages.profile_emails_notifications.competence_result'),
                      ['competenceacquired', 'competencefailed'],
                      'notification-competence-result'
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailsAndNotifications;
